"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createDynamicQuestionnaireSelectors = exports.liftContextSelectors = void 0;
const memoize_1 = __importDefault(require("lodash/memoize"));
const forEach_1 = __importDefault(require("lodash/forEach"));
const reselect_1 = require("reselect");
const questionnaire_1 = require("@zedoc/questionnaire");
const form_values_1 = require("@zedoc/form-values");
const selectors_1 = require("@zedoc/selectors");
const liftContextSelectors = (selectContext) => {
    const select = {};
    (0, forEach_1.default)([
        'questionnaire',
        'validationErrors',
        'formValues',
        'dynamicProperties',
        'hasValidationErrors',
        'formErrors',
        'touched',
        'evaluationScope',
        'dynamicQuestionnaire',
        'questionCursor',
        'context',
        'name',
        'options',
        'meta',
    ], (name) => {
        select[name] = (...args) => (0, selectors_1.higherOrderSelector)(selectContext, (context) => context.select[name](...args));
    });
    return select;
};
exports.liftContextSelectors = liftContextSelectors;
const createDynamicQuestionnaireSelectors = ({ selectRawFormValues, selectRawTouched, selectVariables, selectValidationErrors, selectPropertiesOverrides, selectQuestionnaire, selectFlatSections, }) => {
    const selectEmptyEvaluationScope = (0, reselect_1.createSelector)((0, selectors_1.toSelector)(selectVariables), (0, selectors_1.toSelector)(selectQuestionnaire), (variables, questionnaire) => new questionnaire_1.EvaluationScope({
        questionnaire,
        variables,
        answers: {},
    }));
    const selectInitialValues = (0, memoize_1.default)(() => (0, reselect_1.createSelector)(selectEmptyEvaluationScope, (evaluationScope) => evaluationScope.getInitialValues()));
    const selectRawFormValuesAndInitialValues = (0, selectors_1.reconcilingSelector)((0, selectors_1.toSelector)(selectRawFormValues), selectInitialValues(), (rawFormValues, initialValues) => (0, form_values_1.mergeFormValues)(initialValues, rawFormValues));
    const selectStaticEvaluationScope = (0, reselect_1.createSelector)(selectEmptyEvaluationScope, selectRawFormValuesAndInitialValues, (emptyEvaluationScope, formValues) => emptyEvaluationScope.copyWithFormValues(formValues));
    const selectFormValuesAndDynamicProperties = (0, selectors_1.reconcilingSelector)(selectStaticEvaluationScope, (0, selectors_1.toSelector)(selectPropertiesOverrides), (evaluationScope, propertiesOverrides) => (0, questionnaire_1.evaluateFormValuesAndProperties)(evaluationScope, {
        propertiesOverrides,
    }));
    const selectFormValues = (0, memoize_1.default)(() => (0, reselect_1.createSelector)(selectFormValuesAndDynamicProperties, (0, selectors_1.argument)(0, 'formValues')));
    const selectDynamicProperties = (0, memoize_1.default)(() => (0, reselect_1.createSelector)(selectFormValuesAndDynamicProperties, (0, selectors_1.argument)(0, 'properties')));
    const selectRootEvaluationScope = (0, memoize_1.default)(() => (0, reselect_1.createSelector)(selectStaticEvaluationScope, selectFormValues(), (staticEvaluationScope, formValues) => staticEvaluationScope.copyWithFormValues(formValues)));
    const dynamicVariables = (0, memoize_1.default)(() => (0, reselect_1.createSelector)(selectRootEvaluationScope(), (evaluationScope) => evaluationScope.evaluateVariables()));
    const select = {
        questionnaire: (0, selectors_1.constant)((0, selectors_1.toSelector)(selectQuestionnaire)),
        validationErrors: (0, selectors_1.constant)((0, selectors_1.toSelector)(selectValidationErrors)),
        formValues: selectFormValues,
        dynamicVariables,
        variable: (key) => (0, reselect_1.createSelector)(dynamicVariables(), (0, selectors_1.createGetAtKey)(key)),
        dynamicProperties: selectDynamicProperties,
        hasValidationErrors: (0, memoize_1.default)(() => (0, reselect_1.createSelector)((0, selectors_1.toSelector)(selectQuestionnaire), (0, selectors_1.toSelector)(selectValidationErrors), selectDynamicProperties(), (questionnaire, formErrors, properties) => !!(0, form_values_1.cleanEmptyValues)((0, questionnaire_1.maskHiddenFormValues)(questionnaire, formErrors, properties)))),
        formErrors: (0, memoize_1.default)(() => (0, selectors_1.reconcilingSelector)((0, selectors_1.toSelector)(selectQuestionnaire), selectFormValues(), (0, selectors_1.toSelector)(selectValidationErrors), selectDynamicProperties(), (questionnaire, formValues, formErrors, properties) => (0, questionnaire_1.getFormErrors)(questionnaire, formValues, {
            properties,
            overwrite: formErrors,
            skipHidden: true,
        }))),
        touched: (0, memoize_1.default)(() => (0, reselect_1.createSelector)((0, selectors_1.toSelector)(selectQuestionnaire), (0, selectors_1.toSelector)(selectRawTouched), selectDynamicProperties(), 
        // TODO: Explain why exactly we need to mask here. This was causing problems
        //       because "touched" object does not have "value: []" for collections
        //       and as a result everything was masked inside collection.
        (questionnaire, touched, properties) => (0, questionnaire_1.maskHiddenFormValues)(questionnaire, touched, properties))),
        evaluationScope: (selectScopeKey) => (0, reselect_1.createSelector)(selectRootEvaluationScope(), (0, selectors_1.toSelector)(selectScopeKey), (rootEvaluationScope, scopeKey) => rootEvaluationScope.getScopeForScopeKey(scopeKey)),
        dynamicQuestionnaire: (selectScopeKey) => (0, reselect_1.createSelector)((0, selectors_1.toSelector)(selectQuestionnaire), selectDynamicProperties(), (0, selectors_1.toSelector)(selectScopeKey), (questionnaire, properties, scopeKey) => (0, questionnaire_1.getDynamicQuestionnaire)(questionnaire, properties, scopeKey)),
        questionCursor: (selectQuestionId, selectHierarchy, { force = false } = {}) => {
            const isValidScreen = (cursor) => {
                // NOTE: By convention we are only showing prologue for sections
                //       and epilogue for collections. In the future, we will probably
                //       want it to be configurable at questionnaire ui level.
                if (cursor.isEpilogue() && cursor.isSection()) {
                    return false;
                }
                if (cursor.isPrologue() && cursor.isCollection()) {
                    return false;
                }
                return true;
            };
            const defaultStepFilter = (cursor) => isValidScreen(cursor) && cursor.isVisible();
            //------------------------------------------------------------------------------
            return (0, reselect_1.createSelector)((0, selectors_1.toSelector)({
                questionnaire: selectQuestionnaire,
                properties: selectDynamicProperties(),
                formValues: selectFormValues(),
                questionId: selectQuestionId,
                hierarchy: selectHierarchy,
                flatSections: selectFlatSections,
            }), (params) => {
                if (force) {
                    // NOTE: If "force" is used we seek to the given question even if it is not visible.
                    return questionnaire_1.QuestionCursor.begin(Object.assign(Object.assign({}, params), { stepFilter: isValidScreen })).setFilters({
                        stepFilter: defaultStepFilter,
                    });
                }
                return questionnaire_1.QuestionCursor.begin(Object.assign(Object.assign({}, params), { stepFilter: defaultStepFilter }));
            });
        },
    };
    return select;
};
exports.createDynamicQuestionnaireSelectors = createDynamicQuestionnaireSelectors;
//# sourceMappingURL=selectors.js.map